import { ChevronDown } from 'lucide-react';
import { Accordion as AccordionPrimitive } from 'radix-ui';
import type * as React from 'react';

import { cn } from '@/lib/utils';

function Accordion({ className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={cn('space-y-3', className)}
      {...props}
    />
  );
}

// Each item is its own bordered, rounded card — not a divided list — so
// `surface-card` (fill + border colour) and `rounded-xl` (the `--radius-card`
// role, same as every other content card in the app) live here rather than
// the shadcn-default `border-b` list-row look.
function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('surface-card overflow-hidden rounded-xl border', className)}
      {...props}
    />
  );
}

// Left side (icon/title/description) is caller-supplied `children` — this
// only owns the row layout, the 16px vertical padding, and the chevron.
// The chevron rotates via `group-data-[state=open]`, not a `>svg` selector,
// since it's now wrapped in a circle rather than a bare direct child.
function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'group flex flex-1 items-center justify-between gap-4 px-5 py-4 text-left outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {children}
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-lime text-foreground transition-transform duration-200 group-data-[state=open]:rotate-180">
          <ChevronDown className="size-4" />
        </span>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn('px-5 pb-5', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
